// @flow

import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import type { Persona, Note, SignOutActionCreator } from '../../typings';
import AnimatedRow from '../Forms/AnimatedRow';
import ButtonContainer from '../Forms/ButtonContainer';
import RowIcon from '../Forms/RowIcon';
import RowContent from '../Forms/RowContent';
import FormContainer from '../Forms/FormContainer';
import Label from '../Text/Label';
import ContentText from '../Text/ContentText';
import Button from '../Button';
import GroupPicker from '../GroupPicker';
import FormScreenContainer from '../ui/FormScreenContainer';
import PaddedContainer from '../ui/PaddedContainer';
import dateIcon from '../../../assets/svg/icons/date-gray.svg';
import locationIcon from '../../../assets/svg/icons/location-gray.svg';
import positionIcon from '../../../assets/svg/icons/position-gray.svg';
import TimeClockNote from '../../../timeClock/components/TimeClockNote';
import FormFieldsContainer from '../ui/FormFieldsContainer';

const FormFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 40px;
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  padding-left: 8px;
  padding-right: 8px;
`;

const StyledButtonContainer = styled(ButtonContainer)`
  animation-delay: ${props => (props.useLongDelayAnimation ? '1.2s' : '0.4s')};
`;

type Props = {
  personas?: Array<Persona>,
  notes: Array<Note> | null,
  currentStep: number,
  deviceHeight: number,
  useLongDelayAnimation: boolean,
  isIntegrationKiosk: boolean,
  clockInNextStep: (
    location?: Persona | null,
    position?: Persona | null,
    timeClockNote?: string | null
  ) => void,
  clockInPrevStep: () => void,
  signOut: SignOutActionCreator
};

type State = {
  location: Persona | null,
  position: Persona | null,
  locations: Array<Persona>,
  positions: Array<Persona>,
  timeClockNote: string
};

export default class NoShiftFields extends PureComponent<Props, State> {
  componentDidMount() {
    if (this.props.personas) {
      this.setGroups(this.props.personas);
    }
  }

  state = {
    location: null,
    position: null,
    locations: [],
    positions: [],
    timeClockNote: ''
  };

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps({ personas }: Props) {
    if (personas && personas !== this.props.personas) {
      this.setGroups(personas);
    }
  }

  setGroups = (personas: Array<Persona>) => {
    const newState = {};

    newState.locations = personas.filter(persona => persona.type === 'location');
    newState.positions = personas.filter(persona => persona.type === 'position');

    if (newState.locations.length === 1) {
      newState.location = newState.locations[0];
    }

    if (newState.positions.length === 1) {
      newState.position = newState.positions[0];
    }

    this.setState(newState);
  };

  selectLocation = (location: Persona) => {
    this.setState({ location });
    this.props.clockInNextStep();
  };

  selectPosition = (position: Persona) => {
    this.setState({ position });
    this.props.clockInNextStep();
  };

  unselectLocation = () => {
    this.setState({ location: null });
    this.props.clockInPrevStep();
  };

  unselectPosition = () => {
    this.setState({ position: null });
    this.props.clockInPrevStep();
  };

  canClockIn = () => {
    return Boolean(this.state.locations.length && this.state.positions.length);
  };

  getNote = (note: string) => this.setState({ timeClockNote: note });

  renderStep0() {
    if (this.state.location && this.state.position) {
      return this.renderStep3(false);
    }

    return (
      <FormScreenContainer>
        <FormContainer>
          <FormFieldsWrapper>
            <PaddedContainer>{this.renderActionButtons(true, false)}</PaddedContainer>
          </FormFieldsWrapper>
        </FormContainer>
      </FormScreenContainer>
    );
  }

  renderStep1(locations: Array<Persona>) {
    return (
      <GroupPicker
        group="location"
        items={locations}
        back={this.unselectLocation}
        cancel={this.props.signOut}
        proceed={this.selectLocation}
        deviceHeight={this.props.deviceHeight}
      />
    );
  }

  renderStep2(positions: Array<Persona>) {
    return (
      <GroupPicker
        group="position"
        items={positions}
        back={this.unselectPosition}
        cancel={this.props.signOut}
        proceed={this.selectPosition}
        deviceHeight={this.props.deviceHeight}
      />
    );
  }

  renderStep3(includeBackButton: boolean) {
    const { location, position } = this.state;

    if (location === null || position === null) {
      return null;
    }

    const { notes, useLongDelayAnimation, deviceHeight } = this.props;
    const {
      locationAnimationDelay,
      positionAnimationDelay,
      todaysNotesAnimationDelay,
      timeClockNotesAnimationDelay
    } = this.getAnimationDelays(position, notes, useLongDelayAnimation);

    return (
      <FormScreenContainer>
        <FormContainer>
          <FormFieldsContainer deviceHeight={deviceHeight}>
            <PaddedContainer>
              <AnimatedRow borderTop borderBottom delay={locationAnimationDelay}>
                <RowIcon src={locationIcon} />
                <RowContent>
                  <Label>Location</Label>
                  <ContentText>{location.name}</ContentText>
                </RowContent>
              </AnimatedRow>

              {position.type === 'position' && (
                <AnimatedRow borderBottom delay={positionAnimationDelay}>
                  <RowIcon src={positionIcon} />
                  <RowContent>
                    <Label>Position</Label>
                    <ContentText color={position.color}>{position.name}</ContentText>
                  </RowContent>
                </AnimatedRow>
              )}

              {Boolean(notes && notes.length) && (
                <AnimatedRow borderBottom delay={todaysNotesAnimationDelay}>
                  <RowIcon src={dateIcon} />
                  <RowContent>
                    <Label>Todays notes</Label>
                    {((notes: any): Array<Note>).map(note => (
                      <ContentText key={note.id}>{note.content}</ContentText>
                    ))}
                  </RowContent>
                </AnimatedRow>
              )}

              <TimeClockNote
                adHocClockIn
                delay={timeClockNotesAnimationDelay}
                getNoteForAdHoc={this.getNote.bind(this)}
              />
            </PaddedContainer>
          </FormFieldsContainer>

          <ButtonsWrapper>{this.renderActionButtons(false, includeBackButton)}</ButtonsWrapper>
        </FormContainer>
      </FormScreenContainer>
    );
  }

  renderActionButtons(noMargin: boolean, includeBackButton: boolean) {
    return (
      <PaddedContainer>
        <StyledButtonContainer
          noMargin={noMargin}
          useLongDelayAnimation={this.props.useLongDelayAnimation}>
          {this.canClockIn() && (
            <Button
              label="Clock in"
              buttonTheme="grey"
              themeColor="btnGreenTextColor"
              onPress={() =>
                this.props.clockInNextStep(
                  this.state.location,
                  this.state.position,
                  this.state.timeClockNote
                )
              }
            />
          )}
          {includeBackButton && (
            <Button
              label="Back"
              buttonTheme="grey"
              onPress={this.props.clockInPrevStep}
              withMargin
            />
          )}
          {!this.props.isIntegrationKiosk && (
            <Button label="Cancel" buttonTheme="grey" onPress={this.props.signOut} withMargin />
          )}
        </StyledButtonContainer>
      </PaddedContainer>
    );
  }

  getAnimationDelays(
    position?: Persona | null,
    notes?: Array<Note> | null,
    useLongDelayAnimation: boolean
  ) {
    const initDelay = useLongDelayAnimation ? 0.4 : 0;

    const locationAnimationDelay = initDelay + 0.4;
    let lastAnimationDelay = locationAnimationDelay;

    let positionAnimationDelay;
    if (position?.type === 'position') {
      lastAnimationDelay = positionAnimationDelay = lastAnimationDelay + 0.2;
    }

    let todaysNotesAnimationDelay;
    if (notes && notes.length) {
      lastAnimationDelay = todaysNotesAnimationDelay = lastAnimationDelay + 0.2;
    }

    const timeClockNotesAnimationDelay = lastAnimationDelay + 0.2;

    return {
      locationAnimationDelay,
      positionAnimationDelay,
      todaysNotesAnimationDelay,
      timeClockNotesAnimationDelay
    };
  }

  render() {
    const { personas, currentStep } = this.props;
    if (!personas) {
      return null;
    }

    switch (currentStep) {
      case 1:
        return this.renderStep1(this.state.locations);
      case 2:
        return this.renderStep2(this.state.positions);
      case 3:
        return this.renderStep3(true);
      default:
        return this.renderStep0();
    }
  }
}

NoShiftFields.propTypes = {
  personas: PropTypes.array,
  currentStep: PropTypes.number,
  clockInNextStep: PropTypes.func,
  clockInPrevStep: PropTypes.func,
  notes: PropTypes.array,
  signOut: PropTypes.func,
  useLongDelayAnimation: PropTypes.bool,
  isIntegrationKiosk: PropTypes.bool
};
